import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [examDropdownOpen, setExamDropdownOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setExamDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const examCategories = {
    'RBI & NABARD': [
      { id: 'rbi-grade-b', name: 'RBI Grade B', icon: '🏛️' },
      { id: 'nabard-grade-a', name: 'NABARD Grade A', icon: '🌾' }
    ],
    'SSC Exams': [
      { id: 'ssc-cgl', name: 'SSC CGL', icon: '📚' },
      { id: 'ssc-chsl', name: 'SSC CHSL', icon: '✏️' },
      { id: 'ssc-mts', name: 'SSC MTS', icon: '🔧' },
      { id: 'ssc-cpo', name: 'SSC CPO', icon: '👮' },
      { id: 'ssc-gd', name: 'SSC GD', icon: '🛡️' },
      { id: 'ssc-steno', name: 'SSC Steno', icon: '⌨️' }
    ],
    'Bank PO': [
      { id: 'sbi-po', name: 'SBI PO', icon: '🏦' },
      { id: 'ibps-po', name: 'IBPS PO', icon: '🏛️' },
      { id: 'ibps-rrb-po', name: 'IBPS RRB PO', icon: '🌾' }
    ],
    'Bank Clerk': [
      { id: 'sbi-clerk', name: 'SBI Clerk', icon: '💼' },
      { id: 'ibps-clerk', name: 'IBPS Clerk', icon: '📋' },
      { id: 'ibps-rrb-clerk', name: 'IBPS RRB Clerk', icon: '📝' }
    ]
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_vidya365-coaching/artifacts/051wobd3_IMG-20260204-WA0032.jpg" 
              alt="Vidya365" 
              className="h-14 w-auto shadow-md rounded-sm"
            />
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            <Link 
              to="/" 
              className={`font-medium transition-colors ${isActive('/') ? 'text-red-600' : 'text-gray-700 hover:text-red-600'}`}
            >
              Home
            </Link>
            
            {/* Exam Info Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setExamDropdownOpen(!examDropdownOpen)}
                className={`font-medium transition-colors flex items-center gap-1 ${
                  isActive('/exams') || location.pathname.startsWith('/exams/') 
                    ? 'text-red-600' 
                    : 'text-gray-700 hover:text-red-600'
                }`}
              >
                Exam Info
                <ChevronDown className={`w-4 h-4 transition-transform ${examDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Mega Menu Dropdown */}
              {examDropdownOpen && (
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-[800px] bg-white rounded-lg shadow-2xl border border-gray-200 p-6">
                  <div className="grid grid-cols-4 gap-6">
                    {Object.entries(examCategories).map(([category, exams]) => (
                      <div key={category}>
                        <h3 className="font-bold text-sm text-gray-800 mb-3 pb-2 border-b border-red-100">
                          {category}
                        </h3>
                        <ul className="space-y-2">
                          {exams.map((exam) => (
                            <li key={exam.id}>
                              <Link
                                to={`/exams/${exam.id}`}
                                onClick={() => setExamDropdownOpen(false)}
                                className="flex items-center gap-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 px-2 py-1.5 rounded transition-colors"
                              >
                                <span className="text-base">{exam.icon}</span>
                                <span>{exam.name}</span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200 text-center">
                    <Link
                      to="/exams"
                      onClick={() => setExamDropdownOpen(false)}
                      className="text-sm text-red-600 font-semibold hover:text-red-700"
                    >
                      View All Exams →
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <Link 
              to="/results" 
              className={`font-medium transition-colors ${isActive('/results') ? 'text-red-600' : 'text-gray-700 hover:text-red-600'}`}
            >
              Results
            </Link>
            <Link 
              to="/student-benefits" 
              className={`font-medium transition-colors ${isActive('/features') ? 'text-red-600' : 'text-gray-700 hover:text-red-600'}`}
            >
              Features
            </Link>
            <Link 
              to="/study-materials" 
              className={`font-medium transition-colors ${isActive('/resources') ? 'text-red-600' : 'text-gray-700 hover:text-red-600'}`}
            >
              Free Materials
            </Link>
            <Link 
              to="/previous-year-question-paper" 
              className={`font-medium transition-colors ${isActive('/pyq') ? 'text-red-600' : 'text-gray-700 hover:text-red-600'}`}
              data-testid="header-pyq-link"
            >
              Free PYQ
            </Link>
            <Link 
              to="/typing-test" 
              className={`font-medium transition-colors ${isActive('/typing-test') ? 'text-red-600' : 'text-gray-700 hover:text-red-600'}`}
              data-testid="header-typing-link"
            >
              Typing Test
            </Link>
            <Link 
              to="/ai-interview" 
              className={`font-medium transition-colors ${isActive('/ai-interview') ? 'text-red-600' : 'text-gray-700 hover:text-red-600'} relative`}
              data-testid="header-ai-interview-link"
            >
              AI Interview
              <span className="absolute -top-1 -right-3 bg-yellow-400 text-gray-900 text-[8px] font-bold px-1 rounded">NEW</span>
            </Link>
            <Link 
              to="/daily-quiz" 
              className={`font-medium transition-colors ${isActive('/daily-quiz') ? 'text-red-600' : 'text-gray-700 hover:text-red-600'} relative`}
              data-testid="header-daily-quiz-link"
            >
              Daily Quiz
              <span className="absolute -top-1 -right-3 bg-orange-500 text-white text-[8px] font-bold px-1 rounded">🔥</span>
            </Link>
            <a
              href="https://updates.vidya365.in"
              target="_blank"
              rel="noopener noreferrer"
              className="relative font-bold text-white bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-700 hover:to-orange-600 px-3 py-1.5 rounded-full shadow-md transition-all hover:shadow-lg hover:scale-105"
              data-testid="header-latest-updates-link"
            >
              New Updates
              <span className="absolute -top-1 -right-2 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-yellow-500"></span>
              </span>
            </a>
            <Link to="/student/login" className="text-red-600 hover:text-red-700 transition-colors font-medium">
              Student Login
            </Link>
            <Link to="/student/register">
              <Button className="bg-red-600 hover:bg-red-700 text-white">Register Now</Button>
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden text-gray-700"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="lg:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-3">
              <Link 
                to="/" 
                className={`text-left px-4 py-2 rounded ${isActive('/') ? 'bg-red-50 text-red-600' : 'text-gray-700 hover:bg-gray-50'}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Home
              </Link>
              <Link 
                to="/exams" 
                className={`text-left px-4 py-2 rounded ${isActive('/exams') || location.pathname.startsWith('/exams/') ? 'bg-red-50 text-red-600' : 'text-gray-700 hover:bg-gray-50'}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Exam Info
              </Link>
              <Link 
                to="/student-benefits" 
                className={`text-left px-4 py-2 rounded ${isActive('/features') ? 'bg-red-50 text-red-600' : 'text-gray-700 hover:bg-gray-50'}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Features
              </Link>
              <Link 
                to="/study-materials" 
                className={`text-left px-4 py-2 rounded ${isActive('/resources') ? 'bg-red-50 text-red-600' : 'text-gray-700 hover:bg-gray-50'}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Free Materials
              </Link>
              <Link 
                to="/previous-year-question-paper" 
                className={`text-left px-4 py-2 rounded ${isActive('/pyq') ? 'bg-red-50 text-red-600' : 'text-gray-700 hover:bg-gray-50'}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Free PYQ
              </Link>
              <Link 
                to="/typing-test" 
                className={`text-left px-4 py-2 rounded ${isActive('/typing-test') ? 'bg-red-50 text-red-600' : 'text-gray-700 hover:bg-gray-50'}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Typing Test
              </Link>
              <Link 
                to="/ai-interview" 
                className={`text-left px-4 py-2 rounded ${isActive('/ai-interview') ? 'bg-red-50 text-red-600' : 'text-gray-700 hover:bg-gray-50'}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                AI Interview <span className="ml-2 bg-yellow-400 text-gray-900 text-[10px] font-bold px-1.5 py-0.5 rounded">NEW</span>
              </Link>
              <a
                href="https://updates.vidya365.in"
                target="_blank"
                rel="noopener noreferrer"
                className="text-left px-4 py-2 rounded font-bold text-white bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-700 hover:to-orange-600 shadow-md flex items-center justify-between"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-latest-updates-link"
              >
                <span>New Updates</span>
                <span className="ml-2 bg-yellow-400 text-gray-900 text-[10px] font-bold px-1.5 py-0.5 rounded animate-pulse">LIVE</span>
              </a>
              <div className="border-t border-gray-200 my-2"></div>
              <Link 
                to="/student/login" 
                className="text-left px-4 py-2 rounded text-red-600 hover:bg-red-50"
                onClick={() => setMobileMenuOpen(false)}
              >
                Student Login
              </Link>
              <Link 
                to="/student/register" 
                className="mx-4"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Button className="w-full bg-red-600 hover:bg-red-700 text-white">Register Now</Button>
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
